import './Home.css';

import { useState, useMemo, useRef, useEffect } from 'react';
import { Button, message } from 'antd';
import { selectUserId, selectSessionId, setSession } from "../../store/sessionSlice";
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../../components/Navbar/Navbar';
import Steps from '../../components/Steps/Steps';
import CampaignBrief from './CampaignBrief/CampaignBrief';
import CustomerPersona from './CustomerPersona/CustomerPersona';
import CampaignConcept from './CampaignConcept/CampaignConcept';
import ImageGeneration from './ImageGeneration/ImageGeneration';
import Storyboard from './Storyboard/Storyboard';
import SceneManagement from './SceneManagement/SceneManagement';
import VideoGeneration from './VideoGeneration/VideoGeneration';
import Finish from './Finish/Finish';
import { fetchRequest } from '../../utils/Fetcher';
import { auth } from "../../utils/firebase";

const Home = () => {
  const [campaignBrief, setCampaignBrief] = useState(null);
  const [selectedPersonaId, setSelectedPersonaId] = useState(null);
  const [selectedConceptIds, setSelectedConceptIds] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [storyboardPrompt, setStoryboardPrompt] = useState("");
  const [storyboardId, setStoryboardId] = useState("");
  const [sceneState, setSceneState] = useState({});
  const [videoData, setVideoData] = useState([]);

  const [current, setCurrent] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [summary, setSummary] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);

  const [authReady, setAuthReady] = useState(false);

  const userId = useSelector(selectUserId);
  const sessionId = useSelector(selectSessionId);
  const dispatch = useDispatch();
  const hydratedOnceRef = useRef(false);
  const hydratedRef = useRef(false);

  const stepsItem = useMemo(() => ([
    {
      title: 'Set Settings',
      content: <CampaignBrief
        onNext={(values) => {
          setCampaignBrief(values);
          goTo(1);
        }}
        initialData={campaignBrief}
      />,
    },
    {
      title: 'Customer Persona',
      content: <CustomerPersona onNext={(personaId) => {
        setSelectedPersonaId(personaId);
        goTo(2);
      }}/>,
    },
    {
      title: 'Campaign Concept',
      content: <CampaignConcept
      selectedPersonaId={selectedPersonaId}
      onNext={(conceptsIds) => {
        setSelectedConceptIds(conceptsIds);
        goTo(3);
      }}
    />,
    },
    {
      title: 'Image Generation',
      content: <ImageGeneration
        generatedImages={generatedImages}
        setGeneratedImages={setGeneratedImages}
        selectedConceptIds={selectedConceptIds}
        onNext={() => goTo(4)}
      />,
    },
    {
      title: 'Storyboard',
      content: <Storyboard
        selectedPersonaId={selectedPersonaId}
        selectedConceptIds={selectedConceptIds}
        scenes={scenes}
        setScenes={setScenes}
        storyboardId={storyboardId}
        setStoryboardId={setStoryboardId}
        descriptionPrompt={storyboardPrompt}
        setDescriptionPrompt={setStoryboardPrompt}
        onNext={() => goTo(5)}
      />,
    },
    {
      title: 'Scene Management',
      content: <SceneManagement
        scenes={scenes}
        storyboardId={storyboardId}
        generatedImages={generatedImages}
        selectedConceptIds={selectedConceptIds}
        onNext={() => goTo(6)}
        sceneState={sceneState}
        setSceneState={setSceneState}
      />,
    },
    {
      title: 'Video Generation',
      content: <VideoGeneration
        scenes={scenes}
        storyboardId={storyboardId}
        onNext={() => goTo(7)}
        sceneState={sceneState}
        videoData={videoData}
        setVideoData={setVideoData}
      />,
    },
    {
      title: 'Finish',
      content: <Finish />,
    },
  ]), [
    campaignBrief, selectedPersonaId, selectedConceptIds, scenes, generatedImages,
    storyboardId, storyboardPrompt, sceneState, videoData
  ]);

  const handleCreateNewSession = async () => {
    const msgKey = "new-session";
    message.loading({ content: "Creating new session...", key: msgKey });

    if (creatingSession) return;
    setCreatingSession(true);

    try {
      if (userId && sessionId) {
        await fetchRequest({
          method: "PATCH",
          path: `auth/${userId}/sessions/${sessionId}`,
          data: JSON.stringify({ status: "expired", expiresAt: new Date().toISOString() }),
        });
      }
    } catch (e) {
      console.warn("Failed to expire old session:", e);
    }

    try {
      const res = await fetchRequest({
        method: "POST",
        path: "auth/sessions/create",
        data: JSON.stringify({
          userId,
          activeAt: new Date().toISOString(),
        }),
      });

      const data = res?.result ?? res?.data ?? res;

      const newSession = {
        userId: data?.userId,
        sessionId: data?.sessionId,
        sessionToken: data?.sessionToken,
      };

      dispatch(setSession(newSession));

      resetAll();

      message.success({ content: "New session created.", key: msgKey });
    } catch (e) {
      console.error("Failed to create new session:", e);
      message.error({ content: "Failed to create new session.", key: msgKey });
    } finally {
      setCreatingSession(false);
    }
  };

  const goTo = (next) => {
    setCurrent(next);
    setMaxReached((prev) => Math.max(prev, next));
  };

  const resetAll = () => {
    hydratedOnceRef.current = false;
    hydratedRef.current = false;
    delete window.__hydratedMergedVideo;
    setSummary(null);
    setCampaignBrief(null);
    setSelectedPersonaId(null);
    setSelectedConceptIds([]);
    setScenes([]);
    setGeneratedImages([]);
    setStoryboardPrompt("");
    setStoryboardId("");
    setSceneState({});
    setVideoData([]);
    setCurrent(0);
    setMaxReached(0);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(() => {
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!sessionId || !authReady) return;

    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchRequest({
          method: "GET",
          path: `status/sessions/${sessionId}/summary`,
        });
        const data = res?.result ?? res?.data ?? res;

        if (!cancelled) setSummary(data);
      } catch (e) {
        console.error("summary failed", e);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [sessionId, authReady]);

  const hydrateData = (cats) => {
    const briefEntry = cats?.campaign_briefs?.items?.entries?.[0] ?? null;
    if (briefEntry) setCampaignBrief(briefEntry);

    const personaId = cats?.customer_personas?.items?.selectedIds?.[0] ?? null;
    if (personaId) setSelectedPersonaId(personaId);

    const conceptIds = cats?.campaign_concepts?.items?.selectedIds ?? [];
    if (conceptIds.length > 0) setSelectedConceptIds(conceptIds);

    const sbId =
      cats?.storyboards?.items?.selectedIds?.[0] ??
      cats?.storyboards?.items?.entries?.[0]?.id ??
      "";
    if (sbId) setStoryboardId(sbId);

    const sceneEntries = cats?.scenes?.items?.entries ?? [];
    if (sceneEntries.length > 0) setScenes(sceneEntries);

    const imgEntries =
      (cats?.campaign_images?.items?.entries?.length
        ? cats?.campaign_images?.items?.entries
        : cats?.images?.items?.entries) ?? [];
    if (imgEntries.length > 0) setGeneratedImages(imgEntries);

    hydrateVideoData(sceneEntries, cats);
    hydrateMergedVideo(cats);
    hydrateStoryboardPrompt(cats);
  };

  const hydrateVideoData = (sceneEntries, cats) => {
    if (!sceneEntries.length) return;
    const normalised = sceneEntries
      .filter((s) => s.videoId && s.video?.state === "SUCCEEDED")
      .map((s) => ({
        sceneId: s.id,
        videoId: s.videoId,
        gcsUri: s.video?.output?.gcsUri ?? null,
        url: null,
        status: "generated",
        approved: false,
        error: null,
        signing: false,
        hasPendingDescriptionChange: false,
        description: s.description ?? s.sceneDescription ?? "",
      }));
    if (normalised.length > 0) setVideoData(normalised);
  };

  const hydrateMergedVideo = (cats) => {
    const vidEntries = cats?.videos?.items?.entries ?? [];
    const latestMerged = vidEntries
      .filter((v) => v.type === "merged" && v.state === "SUCCEEDED")
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
    if (!latestMerged) return;
    window.__hydratedMergedVideo = {
      videoId: latestMerged.id,
      gcsUri: latestMerged.output?.gcsUri ?? latestMerged.gcs_uri ?? latestMerged.gcsUri ?? null,
    };
  };

  const hydrateStoryboardPrompt = (cats) => {
    const sbPromptId = cats?.storyboards?.prompts?.selectedIds?.[0];
    const sbPromptItem = cats?.storyboards?.prompts?.items?.find((p) => p?.id === sbPromptId);
    const sbPromptText = sbPromptItem?.text || sbPromptItem?.prompt || sbPromptItem?.value || "";
    if (sbPromptText) setStoryboardPrompt(sbPromptText);
  };

  const resolveMaxStep = (cats, hasAny) => {
    const done = {
      brief:      hasAny(cats?.campaign_briefs?.items?.selectedIds) || hasAny(cats?.campaign_briefs?.items?.entries),
      persona:    hasAny(cats?.customer_personas?.items?.selectedIds),
      concept:    hasAny(cats?.campaign_concepts?.items?.selectedIds),
      image:      hasAny(cats?.campaign_images?.items?.selectedIds) || hasAny(cats?.images?.items?.selectedIds),
      storyboard: hasAny(cats?.storyboards?.items?.selectedIds),
      scene:      hasAny(cats?.scenes?.items?.entries),
      video:      (cats?.videos?.items?.entries ?? []).some((v) => v.type === "merged" && v.state === "SUCCEEDED"),
    };

    if (done.video)      return 7;
    if (done.scene)      return 6;
    if (done.storyboard) return 5;
    if (done.image)      return 4;
    if (done.concept)    return 3;
    if (done.persona)    return 2;
    if (done.brief)      return 1;
    return 0;
  };

  useEffect(() => {
    if (!summary) return;
    if (hydratedOnceRef.current && hydratedRef.current) return;

    const cats = summary?.categories || {};
    const hasAny = (arr) => Array.isArray(arr) && arr.length > 0;

    if (!hydratedRef.current) {
      hydrateData(cats);
      hydratedRef.current = true;
    }

    if (!hydratedOnceRef.current && current === 0 && maxReached === 0) {
      const maxStep = resolveMaxStep(cats, hasAny);
      setMaxReached(maxStep);
      setCurrent(maxStep);
      hydratedOnceRef.current = true;
    }
  }, [summary, current, maxReached]);

  return (
    <div>
      <Navbar />
      <div className="home-container">
        <div className="steps-area">
            <Steps
              current={current}
              setCurrent={goTo}
              stepsItem={stepsItem.map((s) => ({ title: s.title }))}
              maxReached={maxReached}
            />

              <div className="steps-footer">
              <Button type="primary" loading={creatingSession} onClick={handleCreateNewSession}>
                Create New Session
              </Button>
              </div>
        </div>

        <div className="content-container">
          {stepsItem[current].content}
        </div>
      </div>
    </div>
  );
}

export default Home;